/**
 * Platform administration — create a customer workspace and its first owner (Slice 1b). This is the
 * only screen a platform session can reach; it does not carry a tenant, so none of the tenant-scoped
 * pages are available here.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { ApiError, type CreatedTenant, type TenantRow } from '../api';
import { Wordmark, Button, Banner } from '../ui';

export function Platform(): JSX.Element {
  const { api, signOut } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedTenant | null>(null);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);

  const loadTenants = useCallback(() => {
    api
      .listTenants()
      .then((r) => setTenants(r.tenants))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load workspaces.'));
  }, [api]);
  useEffect(loadTenants, [loadTenants]);

  async function toggleControl(t: TenantRow) {
    setErr(null);
    try {
      await api.updateTenant(t.id, { controlEnabled: !t.control_enabled });
      loadTenants();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update the workspace.');
    }
  }

  // Suggest a slug from the name unless the admin has typed one.
  const [slugEdited, setSlugEdited] = useState(false);
  function onName(v: string) {
    setName(v);
    if (!slugEdited)
      setSlug(
        v
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, ''),
      );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createTenant({
        name: name.trim(),
        slug: slug.trim(),
        owner: { email: ownerEmail.trim(), password: ownerPassword },
      });
      setCreated(r);
      setName('');
      setSlug('');
      setOwnerEmail('');
      setOwnerPassword('');
      setSlugEdited(false);
      loadTenants();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create the tenant.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <Wordmark sub="Platform" />
        <div className="right">
          <div className="who">
            <div className="tenant">Platform admin</div>
            <div className="role">all workspaces</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>
      <main className="main" style={{ maxWidth: 620 }}>
        <div className="page-head">
          <div>
            <h1>New workspace</h1>
            <div className="lead">Create a customer workspace and its first administrator.</div>
          </div>
        </div>

        {err && <Banner kind="err">{err}</Banner>}
        {created && (
          <Banner kind="ok">
            Created <b>{created.tenant.name}</b> (
            <span className="mono">{created.tenant.slug}</span>). Owner{' '}
            <span className="mono">{created.owner.email}</span>{' '}
            {created.owner.created
              ? 'was created and can sign in with the password you set.'
              : 'is an existing account, now a workspace admin (password unchanged).'}
          </Banner>
        )}

        <div className="panel panel-pad">
          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="name">Workspace name</label>
              <input
                id="name"
                className="input"
                placeholder="Vodafone Sites"
                value={name}
                onChange={(e) => onName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label htmlFor="slug">
                Slug <span className="opt">(unique, url-safe)</span>
              </label>
              <input
                id="slug"
                className="input mono"
                placeholder="vodafone"
                value={slug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlug(e.target.value);
                }}
                pattern="[a-z0-9-]+"
                required
              />
            </div>
            <div className="section-label" style={{ marginTop: 20 }}>
              First administrator
            </div>
            <div className="field">
              <label htmlFor="oe">Owner email</label>
              <input
                id="oe"
                className="input"
                type="email"
                placeholder="admin@vodafone.com.au"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="op">
                Temporary password <span className="opt">(new accounts only)</span>
              </label>
              <input
                id="op"
                className="input"
                type="password"
                placeholder="at least 12 characters"
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                minLength={12}
                required
              />
              <div className="hint">
                Ignored if the email already belongs to an account — that user is just granted
                admin.
              </div>
            </div>
            <div className="form-actions">
              <span />
              <Button type="submit" busy={busy}>
                Create workspace
              </Button>
            </div>
          </form>
        </div>

        <div className="section-label" style={{ marginTop: 30 }}>
          Workspaces
        </div>
        <div className="stack">
          {tenants?.map((t) => (
            <div className="row-card" key={t.id}>
              <div>
                <div className="title">{t.name}</div>
                <div className="meta">
                  <span className="mono">{t.slug}</span> · {Number(t.member_count)} member
                  {Number(t.member_count) === 1 ? '' : 's'} · {Number(t.site_count)} site
                  {Number(t.site_count) === 1 ? '' : 's'}
                </div>
              </div>
              <div className="row-right">
                <span className={`pill ${t.control_enabled ? 'good' : 'idle'}`}>
                  <span className="dot" />
                  control {t.control_enabled ? 'enabled' : 'disabled'}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleControl(t)}>
                  {t.control_enabled ? 'Suspend control' : 'Enable control'}
                </button>
              </div>
            </div>
          ))}
          {tenants === null && <div className="empty">Loading…</div>}
        </div>
      </main>
    </div>
  );
}
